"use client";

import { Button } from "@/components/ui/button";
import translations from "@/lib/translations.json";
import {
  ArrowRight,
  BatteryFull,
  CreditCard,
  Download,
  Globe,
  Shield,
  Signal,
  Wifi,
} from "lucide-react";
import Image from "next/image";
import { useRouter } from "next/navigation";
import { useWebsiteLang } from "../context/website-lang-context";

const trustIcons = [CreditCard, Shield, Globe, Download];

export default function Hero() {
  const router = useRouter();
  const { lang } = useWebsiteLang();
  const t = translations[lang].hero;

  const handleCTAClick = () => {
    router.push("/coming-soon");
  };

  return (
    <section className="relative flex min-h-screen items-center overflow-hidden pt-5 sm:pt-20">
      <div className="absolute inset-0 bg-gradient-to-b from-navy-950 via-navy-900 to-navy-950" />
      <div className="absolute inset-0 opacity-30">
        <div className="absolute left-1/4 top-1/4 h-96 w-96 rounded-full bg-amber-500/10 blur-[120px]" />
        <div className="absolute bottom-1/4 right-1/4 h-96 w-96 rounded-full bg-teal-500/10 blur-[120px]" />
      </div>

      <div className="relative mx-auto w-full max-w-7xl px-4 py-12 sm:px-6 sm:py-20 lg:px-8">
        <div className="grid items-center gap-12 lg:grid-cols-2 lg:gap-16">
          <div className="space-y-8">
            <div className="animate-fade-in">
              <span className="inline-flex items-center gap-2 rounded-full border border-amber-500/20 px-4 py-1.5 text-[10px] sm:text-xs font-medium text-amber-400 glass">
                <span className="h-1.5 w-1.5 rounded-full bg-amber-500 animate-pulse-slow" />
                {t.badge}
              </span>
            </div>

            <h1 className="animate-fade-in-up text-3xl font-extrabold !leading-[1.3] tracking-tight sm:text-6xl md:text-6xl lg:text-[40px] xl:text-[55px]">
              <span className="block ">{t.titleLine1}</span>
              <span className="block text-gradient-amber">{t.titleLine2}</span>
            </h1>

            <p className="animate-fade-in-up animate-delay-200 max-w-xl text-base leading-relaxed text-white/60 sm:text-xl xl:text-xl">
              {t.description}
            </p>

            <Button
              onClick={handleCTAClick}
              size="custom"
              className="group inline-flex items-center justify-center gap-2 rounded-lg bg-amber-500 px-4 md:px-7 py-1.5 md:py-3.5 text-base md:text-lg font-semibold text-blue-950 transition-all duration-200 hover:bg-amber-400 hover:shadow-lg hover:shadow-amber-500/25"
            >
              {t.cta}
              <ArrowRight
                size={18}
                className="transition-transform group-hover:translate-x-0.5"
              />
            </Button>

            <div className="grid grid-cols-2 gap-4 pt-2 animate-fade-in-up animate-delay-400 sm:grid-cols-4">
              {t.trustItems.map((item, index) => {
                const Icon = trustIcons[index] ?? Globe;
                return (
                  <div
                    key={item}
                    className="flex items-center gap-2 text-xs text-white/40"
                  >
                    <Icon size={14} className="shrink-0 text-amber-500/60" />
                    {item}
                  </div>
                );
              })}
            </div>
          </div>

          <div className="relative animate-[fadeIn_1s_ease-out]">
            <div className="absolute inset-0 animate-pulse-slow rounded-3xl bg-gradient-to-r from-[hsl(37,100%,54%)] to-[hsl(37,100%,54%)] opacity-20 blur-xl sm:blur-3xl" />
            <div className="relative overflow-hidden rounded-3xl  sm:border-[5px] border-[hsl(221,51%,16%)]/20 shadow-2xl transition-transform duration-500 hover:scale-[1.02]">
              <Image
                src="/images/website/shifts.jpg"
                alt={t.imageAlt}
                width={800}
                height={600}
                className="h-auto w-full"
                priority
              />
            </div>
            {/* Mobile preview overlapping the bottom-right corner of the desktop image */}
            {/* Same look as the form preview in settings: dark bezel, small corner radius and a
              notch, plus a status bar (time, signal, wifi, battery) above the screenshot. */}
            <div
              className="absolute 
            -bottom-8
            right-3
            w-[25%]
            max-w-[154px] 
            rounded-[1.1rem] border-[3px] border-neutral-900 bg-neutral-900 shadow-2xl shadow-black/50 ring-1 ring-white/10 sm:rounded-[1.4rem] sm:border-4"
            >
              <div className="relative overflow-hidden rounded-[0.8rem] bg-white sm:rounded-[1.05rem]">
                <div
                  className="absolute left-1/2 top-[3px] z-10 h-1.5 w-6 -translate-x-1/2 rounded-full bg-neutral-900 sm:h-2.5 sm:w-12"
                  aria-hidden
                />
                <div
                  className="flex items-center justify-between bg-white px-2 pb-0.5 pt-1 text-[6px] font-semibold leading-none text-neutral-900 sm:px-4 sm:pt-1.5 sm:text-[9px]"
                  aria-hidden
                >
                  <span>9:41</span>
                  <span className="flex items-center gap-0.5 sm:gap-1">
                    <Signal
                      className="h-2 w-2 sm:h-2.5 sm:w-2.5"
                      strokeWidth={2.5}
                    />
                    <Wifi
                      className="h-2 w-2 sm:h-2.5 sm:w-2.5"
                      strokeWidth={2.5}
                    />
                    <BatteryFull
                      className="h-2.5 w-2.5 sm:h-3 sm:w-3"
                      strokeWidth={2}
                    />
                  </span>
                </div>
                <Image
                  src="/images/website/shifts-mobile.jpg"
                  alt={t.imageMobileAlt}
                  width={780}
                  height={1688}
                  sizes="(min-width: 1024px) 240px, 34vw"
                  className="h-auto w-full"
                  priority
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
